/**
 * Configurable store-wide size/length option lists (admin-managed).
 * Built-in bracelet, ring, and chain seeds ship in code; admins can add more without deploys.
 */

/** @typedef {{ value: string, label: string, priceDelta?: number, image?: string }} JewelryOption */
/** @typedef {{ label: string, shortLabel?: string, showPrice?: boolean, builtin?: boolean, options: JewelryOption[] }} StoreOptionList */

/** @type {JewelryOption[]} */
export const DEFAULT_CHAIN_OPTIONS = [
  { value: '14', label: '14″ choker', priceDelta: 0 },
  { value: '16', label: '16″ standard', priceDelta: 0 },
  { value: '18', label: '18″', priceDelta: 150 },
  { value: '20', label: '20″', priceDelta: 300 },
  { value: '22', label: '22″ long', priceDelta: 450 },
  { value: 'none', label: 'Pendant only (no chain)', priceDelta: -100 },
];

/** @type {JewelryOption[]} */
export const DEFAULT_RING_SIZES = [
  { value: '5', label: 'Size 5 (US)', priceDelta: 0 },
  { value: '6', label: 'Size 6', priceDelta: 0 },
  { value: '7', label: 'Size 7', priceDelta: 0 },
  { value: '8', label: 'Size 8', priceDelta: 0 },
  { value: '9', label: 'Size 9', priceDelta: 0 },
  { value: 'adjustable', label: 'Adjustable / free size', priceDelta: 0 },
];

/** @type {JewelryOption[]} */
export const DEFAULT_BRACELET_SIZES = [
  { value: '6', label: '6″ (petite)', priceDelta: 0 },
  { value: '7', label: '7″ (standard)', priceDelta: 0 },
  { value: '8', label: '8″', priceDelta: 0 },
  { value: 'adjustable', label: 'Adjustable with extender', priceDelta: 0 },
];

/** @type {Record<string, StoreOptionList>} */
export const BUILTIN_STORE_LIST_SEEDS = {
  bracelet: {
    label: 'Bracelet / bangle',
    shortLabel: 'Bracelet',
    showPrice: false,
    builtin: true,
    options: DEFAULT_BRACELET_SIZES,
  },
  ring: {
    label: 'Ring sizes',
    shortLabel: 'Ring',
    showPrice: false,
    builtin: true,
    options: DEFAULT_RING_SIZES,
  },
  chain: {
    label: 'Chain lengths',
    shortLabel: 'Chain',
    showPrice: true,
    builtin: true,
    options: DEFAULT_CHAIN_OPTIONS,
  },
};

/** Legacy per-product override keys for the three built-in lists. */
export const LEGACY_PRODUCT_FORM_KEYS = {
  chain: 'chainOptions',
  ring: 'ringOptions',
  bracelet: 'braceletOptions',
};

/** @type {Record<string, StoreOptionList>} */
let runtimeStoreOptionLists = cloneBuiltinStoreLists();

function cloneOptions(options) {
  return (options || []).map((o) => ({ ...o }));
}

function cloneBuiltinStoreLists() {
  /** @type {Record<string, StoreOptionList>} */
  const out = {};
  for (const [id, seed] of Object.entries(BUILTIN_STORE_LIST_SEEDS)) {
    out[id] = {
      label: seed.label,
      shortLabel: seed.shortLabel,
      showPrice: Boolean(seed.showPrice),
      builtin: true,
      options: cloneOptions(seed.options),
    };
  }
  return out;
}

/** @param {JewelryOption[]} rows */
export function sanitizeStoreListOptions(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const [index, row] of rows.entries()) {
    if (!row || typeof row !== 'object') continue;
    const label = String(row.label ?? '').trim();
    if (!label) continue;
    let value = String(row.value ?? '').trim();
    if (!value) value = `option_${index + 1}`;
    const entry = { value: value.slice(0, 32), label: label.slice(0, 120) };
    if (row.priceDelta != null && row.priceDelta !== '') {
      const n = Number(row.priceDelta);
      if (Number.isFinite(n)) entry.priceDelta = n;
    }
    const image = String(row.image ?? '').trim();
    if (image) entry.image = image;
    out.push(entry);
  }
  return out;
}

export function slugifyStoreListId(label) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

function normalizeListEntry(id, raw, { fallbackLabel = '' } = {}) {
  const key = slugifyStoreListId(id);
  if (!key) return null;
  const label = String(raw?.label ?? fallbackLabel).trim();
  if (!label) return null;
  const shortLabel = String(raw?.shortLabel ?? label).trim() || label;
  const options = sanitizeStoreListOptions(raw?.options);
  const seed = BUILTIN_STORE_LIST_SEEDS[key];
  return {
    label: label.slice(0, 120),
    shortLabel: shortLabel.slice(0, 48),
    showPrice: Boolean(raw?.showPrice ?? seed?.showPrice),
    builtin: Boolean(raw?.builtin ?? seed?.builtin),
    options: options.length ? options : cloneOptions(seed?.options || []),
  };
}

/**
 * Accepts new `storeOptionLists`, legacy `storeOptionDefaults`, or flat legacy keys.
 * @param {unknown} raw
 * @returns {Record<string, StoreOptionList>}
 */
export function normalizeStoreOptionLists(raw) {
  const data = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  /** @type {Record<string, StoreOptionList>} */
  const out = cloneBuiltinStoreLists();

  const listsRaw =
    data.storeOptionLists && typeof data.storeOptionLists === 'object' && !Array.isArray(data.storeOptionLists)
      ? data.storeOptionLists
      : null;

  if (listsRaw) {
    for (const [id, entry] of Object.entries(listsRaw)) {
      const normalized = normalizeListEntry(id, entry);
      if (normalized) out[slugifyStoreListId(id)] = normalized;
    }
  }

  const legacyDefaults =
    data.storeOptionDefaults && typeof data.storeOptionDefaults === 'object'
      ? data.storeOptionDefaults
      : data;

  const legacyMap = {
    bracelet: legacyDefaults.braceletOptions,
    ring: legacyDefaults.ringOptions,
    chain: legacyDefaults.chainOptions,
  };
  for (const [id, options] of Object.entries(legacyMap)) {
    const cleaned = sanitizeStoreListOptions(options);
    if (cleaned.length) {
      out[id] = { ...out[id], options: cleaned };
    }
  }

  return out;
}

/** @param {Record<string, StoreOptionList>|null|undefined} lists */
export function setStoreOptionLists(lists) {
  runtimeStoreOptionLists = normalizeStoreOptionLists(lists || {});
}

/** Backward-compatible alias used by site settings loader. */
export function setJewelryStoreOptionDefaults(raw) {
  setStoreOptionLists(raw);
}

export function getStoreOptionLists() {
  return runtimeStoreOptionLists;
}

/** @deprecated use getStoreOptionLists */
export function getJewelryStoreOptionDefaults() {
  return runtimeStoreOptionLists;
}

export function getStoreOptionListIds() {
  return Object.keys(runtimeStoreOptionLists);
}

/** @param {string} id */
export function getStoreOptionListMeta(id) {
  const key = slugifyStoreListId(id);
  return key ? runtimeStoreOptionLists[key] || null : null;
}

/** @param {string} key */
export function getStoreJewelryOptions(key) {
  const id = slugifyStoreListId(key);
  const list = id ? runtimeStoreOptionLists[id] : null;
  if (list?.options?.length) return list.options;
  return getBuiltinJewelryOptions(id);
}

/** Hard-coded fallbacks before admin store defaults are loaded. */
export function getBuiltinJewelryOptions(key) {
  const id = slugifyStoreListId(key);
  const seed = BUILTIN_STORE_LIST_SEEDS[id];
  return seed ? cloneOptions(seed.options) : [];
}

/** @param {string} optionsKey */
export function storeListShowsPrice(optionsKey) {
  return Boolean(getStoreOptionListMeta(optionsKey)?.showPrice);
}

/** @param {string} optionsKey */
export function legacyProductFormKeyForStoreList(optionsKey) {
  const id = slugifyStoreListId(optionsKey);
  return LEGACY_PRODUCT_FORM_KEYS[id] || '';
}

/**
 * @param {Record<string, unknown>|null|undefined} productOptions
 * @param {string} optionsKey
 * @returns {JewelryOption[]|null}
 */
export function resolveProductStoreListOverride(productOptions, optionsKey) {
  if (!productOptions || typeof productOptions !== 'object') return null;
  const id = slugifyStoreListId(optionsKey);
  if (!id) return null;

  const legacyKey = LEGACY_PRODUCT_FORM_KEYS[id];
  if (legacyKey && Array.isArray(productOptions[legacyKey]) && productOptions[legacyKey].length) {
    return productOptions[legacyKey];
  }

  const map = productOptions.storeListOptions;
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    const custom = map[id];
    if (Array.isArray(custom) && custom.length) return custom;
  }

  return null;
}

/**
 * Build a product-options patch for a store-list override.
 * @param {string} optionsKey
 * @param {JewelryOption[]|null|undefined} options null clears override
 */
export function productOptionsPatchForStoreList(optionsKey, options) {
  const id = slugifyStoreListId(optionsKey);
  if (!id) return {};

  const legacyKey = LEGACY_PRODUCT_FORM_KEYS[id];
  if (legacyKey) {
    if (!options || !options.length) return { [legacyKey]: undefined };
    return { [legacyKey]: options };
  }

  if (!options || !options.length) {
    return { storeListOptions: { [id]: undefined } };
  }
  return { storeListOptions: { [id]: options } };
}

/**
 * Merge a store-list patch into existing product customization_options.
 * @param {Record<string, unknown>} base
 * @param {Record<string, unknown>} patch
 */
export function mergeProductStoreListPatch(base, patch) {
  const next = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    if (key === 'storeListOptions') {
      const prev =
        next.storeListOptions && typeof next.storeListOptions === 'object' && !Array.isArray(next.storeListOptions)
          ? { ...next.storeListOptions }
          : {};
      const delta = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      for (const [listId, rows] of Object.entries(delta)) {
        if (rows == null || (Array.isArray(rows) && rows.length === 0)) delete prev[listId];
        else prev[listId] = rows;
      }
      if (Object.keys(prev).length) next.storeListOptions = prev;
      else delete next.storeListOptions;
      continue;
    }
    if (value === undefined) delete next[key];
    else next[key] = value;
  }
  return next;
}

/**
 * Collect store list ids referenced by profile field definitions.
 * @param {Array<{ fields?: Array<{ optionsKey?: string }> }>} profiles
 */
export function collectReferencedStoreListIds(profiles) {
  const ids = new Set();
  for (const profile of profiles || []) {
    for (const field of profile?.fields || []) {
      const key = slugifyStoreListId(field?.optionsKey);
      if (key) ids.add(key);
    }
  }
  return ids;
}

export function emptyStoreOptionLists() {
  return cloneBuiltinStoreLists();
}

/** @param {Record<string, StoreOptionList>} lists */
export function serializeStoreOptionListsForSave(lists) {
  const normalized = normalizeStoreOptionLists({ storeOptionLists: lists });
  /** @type {Record<string, StoreOptionList>} */
  const out = {};
  for (const [id, list] of Object.entries(normalized)) {
    out[id] = {
      label: list.label,
      shortLabel: list.shortLabel,
      showPrice: Boolean(list.showPrice),
      builtin: Boolean(list.builtin),
      options: sanitizeStoreListOptions(list.options),
    };
  }
  return out;
}
